import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useUser } from '@clerk/clerk-react';
import { KPI, store } from '@flexo/reducers';
import { navigateToCommunity, navigateToMember } from '@flexo/resolvers';
import { navigateToSite } from '../../../../resolvers/src/lib/site-overview-resolver';
import { HeaderEntityDropdown } from './breadcrumbs-dropdown.utils';

/**
 * FLEXO-6076 #1/#2 + FLEXO-6077 #6: end-user entity selector.
 *
 * A breadcrumb-styled dropdown (reusing HeaderEntityDropdown, the same look as the
 * header breadcrumbs) that lets an end user search and switch across their own
 * SITES and COMMUNITIES in a single combined list. Members are intentionally
 * excluded — the member is always the logged-in end user, so it adds no value here.
 * Replaces the static breadcrumb chain for end-user web sessions.
 *
 * Each group is headed by a contextual aggregate entry — "All sites" (→ the /member
 * aggregate) and "All communities" (→ /communities) — shown only when the user has
 * more than one of that kind. The aggregate currently in view is reflected as the
 * selected entry, so the breadcrumb label tracks where the user is.
 */
type EntityKind = 'site' | 'community' | 'all-sites' | 'all-communities';
type EntityOption = {
  kind: EntityKind;
  id: string;
  name: string;
  // Real id shown as the row subtitle (FLEXO-6088 #1c): community code / site POD.
  subtitle?: string;
  // FLEXO-6088 #1a: sites are nested (indented) under their community header.
  __isChild?: boolean;
  // Drives the aggregate styling in HeaderEntityDropdown's renderOption.
  __allSites?: boolean;
};

const ALL_SITES_ID = '__all_sites__';
const ALL_COMMUNITIES_ID = '__all_communities__';

export function EndUserEntityBreadcrumb() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { user: clerkUser } = useUser();
  const pathSegment = location.pathname.split('/')[1] || '';
  const metadata = useSelector((state: any) => state.metadata);

  const sites = (metadata?.entities as any)?.site || [];
  const communities = (metadata?.entities as any)?.community || [];

  // The end user's own member id (same source the post-auth landing uses to route
  // to /member/:id). On the /member aggregate the route param already carries it.
  const flexoUserId =
    (clerkUser?.publicMetadata as any)?.flexo_user_id ||
    (pathSegment === 'member' ? params.id : undefined);

  const items: EntityOption[] = useMemo(() => {
    // De-dupe by id (entities can repeat across resolver passes).
    const dedupeById = <X extends { [k: string]: any }>(arr: X[], idKey: string) => {
      const seen = new Set<string>();
      return (arr || []).filter((it) => {
        const id = it?.[idKey];
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    };

    const dedupedSites = dedupeById(sites, 'siteId');
    const dedupedCommunities = dedupeById(communities, 'communityId');

    const siteOption = (s: any, isChild: boolean): EntityOption => ({
      kind: 'site',
      id: s.siteId,
      name:
        s.siteName ||
        s.pod ||
        s.siteExternalId ||
        (t('widgets.search.context.site') as string),
      // FLEXO-6088 #1c: POD as the subtitle.
      subtitle: s.pod || s.siteExternalId || '',
      __isChild: isChild,
    });

    // FLEXO-6088 #1d: "All sites" is the always-primary aggregate head (only
    // meaningful when the user has more than one site to aggregate).
    const out: EntityOption[] = [];
    if (dedupedSites.length > 1) {
      out.push({
        kind: 'all-sites',
        id: ALL_SITES_ID,
        name: t('dropdowns.allSites') as string,
        __allSites: true,
      });
    }

    // FLEXO-6088 #1a: nest sites under their community, matching the Figma
    // "Search default directory" (community header → indented sites with POD).
    const usedSiteIds = new Set<string>();
    dedupedCommunities.forEach((c: any) => {
      const code = c.communityCode || c.communityExternalId || '';
      out.push({
        kind: 'community',
        id: c.communityId,
        name:
          c.communityName ||
          c.communityCode ||
          (t('widgets.search.context.community') as string),
        // FLEXO-6088 #1c: community code as the subtitle.
        // FLEXO-6133 #8: prefixed so the bare code has context.
        subtitle: code
          ? `${t('widgets.search.context.community')}: ${code}`
          : '',
      });
      dedupedSites
        .filter((s: any) => s.communityId === c.communityId)
        .forEach((s: any) => {
          usedSiteIds.add(s.siteId);
          out.push(siteOption(s, true));
        });
    });

    // Any sites without a matching community in the list stay at the root level.
    dedupedSites
      .filter((s: any) => !usedSiteIds.has(s.siteId))
      .forEach((s: any) => out.push(siteOption(s, false)));

    return out;
  }, [sites, communities, t]);

  const selected = useMemo<EntityOption | null>(() => {
    // Aggregate views: reflect the relevant "all" entry as selected so the
    // breadcrumb label tracks the current context.
    if (pathSegment === 'member') {
      return items.find((it) => it.kind === 'all-sites') || null;
    }
    if (pathSegment === 'communities') {
      return items.find((it) => it.kind === 'all-communities') || null;
    }
    if (pathSegment === 'community') {
      return (
        items.find((it) => it.kind === 'community' && it.id === params.id) ||
        null
      );
    }
    if (pathSegment === 'site') {
      return (
        items.find((it) => it.kind === 'site' && it.id === params.id) || null
      );
    }
    return null;
  }, [items, pathSegment, params.id]);

  const onSelect = (opt: EntityOption) => {
    if (!opt) return;
    switch (opt.kind) {
      case 'all-sites':
        if (flexoUserId) navigateToMember('member', flexoUserId as string);
        break;
      case 'all-communities':
        store.dispatch(KPI.ResetKpiFields());
        navigate(`/communities${location.search || ''}`);
        break;
      case 'community':
        navigateToCommunity('community', opt.id);
        break;
      default:
        navigateToSite('site', opt.id);
        break;
    }
  };

  return (
    <HeaderEntityDropdown<EntityOption>
      items={items}
      selected={selected}
      getLabel={(it) => it?.name || ''}
      getId={(it) => `${it?.kind}:${it?.id}`}
      // FLEXO-6088 #1c: show the real id (community code / site POD) as subtitle.
      getDescription={(it) => it?.subtitle || ''}
      onSelect={onSelect}
      variant="endUserWeb"
      noOptionsText={t('dropdowns.noResults') as string}
      singleItem={items.length <= 1}
    />
  );
}

export default EndUserEntityBreadcrumb;
